// GHL Feature Parity - Workflow Automation coming soon
/**
 * Jubly - Main Application
 * Email, Calendar & Voice for your team
 */

import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth.jsx';
import EmailApp from './pages/EmailApp';
import CalendarApp from './pages/CalendarApp';
import VoiceApp from './pages/VoiceApp';
import MeetApp from './pages/MeetApp';
import JublyLogo from './components/JublyLogo';
import { BrandingProvider, useBranding } from './hooks/useBranding.jsx';
import BrandMark from './components/BrandMark';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import AdminPage from './pages/AdminPage';
import AdminBugTracker from './pages/AdminBugTracker';
import EquityPage from './pages/EquityPage';
import AccountPage from './pages/AccountPage';
import BillingPage from './pages/BillingPage';
import WorkApp from './pages/WorkApp';
import DocumentEditor from './components/work/DocumentEditor';
import SpreadsheetEditor from './components/work/SpreadsheetEditor';
import PresentationEditor from './components/work/PresentationEditor';
import BrainstormEditor from './components/work/BrainstormEditor';
import CollectiveDocEditor from './components/work/CollectiveDocEditor';
import CollectivePageEditor from './components/work/CollectivePageEditor';
import ContributionWidget from './components/ContributionWidget';
import ContributionsPage from './pages/ContributionsPage';
import ReferralPage from './pages/ReferralPage';
import CallWindow from './components/voice/CallWindow';
import Dialer from './components/voice/Dialer';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import LogoPage from './pages/LogoPage';
import SalesDashboard from './pages/SalesDashboard';
import SalesContacts from './pages/SalesContacts';
import SalesPipeline from './pages/SalesPipeline';
import SalesTasks from './pages/SalesTasks';
import SalesCompanies from './pages/SalesCompanies';
import SalesCampaigns from './pages/SalesCampaigns';
import SalesReports from './pages/SalesReports';
import MarketingApp from './pages/MarketingApp';
import PresenterMode from './components/work/slides/PresenterMode';
import EmbedSlideViewer from './components/work/slides/EmbedSlideViewer';
import WorkflowList from './components/WorkflowList';
import WorkflowBuilder from './components/WorkflowBuilder';
import WorkflowAiPage from './pages/WorkflowAiPage';
import WorkflowAnalyticsPage from './pages/WorkflowAnalyticsPage';
import WorkflowBatchApprovalPage from './pages/WorkflowBatchApprovalPage';
import WorkflowTemplatesPage from './pages/WorkflowTemplatesPage';

// Voice/SMS product is turned off (Telnyx numbers released Aug 2026 to stop
// monthly charges). Flip to true to re-enable the Voice product across the app.
const VOICE_ENABLED = false;

// Detect default route from subdomain (calendar.jubly.net → /calendar, voice.jubly.net → /voice, etc.)
function getDefaultRoute() {
  const hostname = window.location.hostname;
  if (hostname.startsWith('calendar.') || hostname.startsWith('cal.')) return '/calendar';
  if (VOICE_ENABLED && (hostname.startsWith('voice.') || hostname.startsWith('phone.'))) return '/voice';
  if (hostname.startsWith('mail.') || hostname.startsWith('email.')) return '/email';
  if (hostname.startsWith('admin.')) return '/admin';
  return '/email';
}

// User menu dropdown
const AVATAR_COLORS = [
  'from-blue-500 to-purple-500',
  'from-emerald-500 to-teal-500',
  'from-orange-500 to-red-500',
  'from-pink-500 to-rose-500',
  'from-indigo-500 to-blue-500',
  'from-amber-500 to-yellow-500',
];

function avatarColor(index) {
  return AVATAR_COLORS[index % AVATAR_COLORS.length];
}

function UserMenu({ user, initial }) {
  const [open, setOpen] = useState(false);
  const [committeeCount, setCommitteeCount] = useState(0);
  const { logout, otherAccounts, switchAccount, removeLinkedAccount } = useAuth();

  // Check if user is on any committees
  useState(() => {
    fetch('/api/contributor/committees', { credentials: 'include' })
      .then(r => r.ok ? r.json() : { data: { committees: [] } })
      .then(d => {
        const mine = (d.data?.committees || []).filter(c => c.my_role);
        setCommitteeCount(mine.length);
      }).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  const handleAddAccount = () => {
    setOpen(false);
    window.location.href = '/login?add=1';
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 hover:bg-gray-50 rounded-full pl-2 pr-1 py-1 transition-colors"
      >
        <span className="hidden sm:block text-xs text-gray-500">{user?.email || user?.emailAddress}</span>
        <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${avatarColor(0)} flex items-center justify-center shadow-sm`}>
          <span className="text-white text-xs font-semibold">{initial}</span>
        </div>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
            {/* Current account */}
            <div className="px-4 py-2.5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${avatarColor(0)} flex items-center justify-center flex-shrink-0`}>
                  <span className="text-white text-sm font-semibold">{initial}</span>
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{user?.displayName || user?.display_name || 'User'}</div>
                  <div className="text-xs text-gray-500 truncate">{user?.email || user?.emailAddress}</div>
                </div>
                <div className="ml-auto">
                  <span className="inline-block w-2 h-2 rounded-full bg-green-400" title="Active" />
                </div>
              </div>
            </div>

            {/* Other linked accounts */}
            {otherAccounts.length > 0 && (
              <div className="border-b border-gray-100 py-1">
                <div className="px-4 py-1.5">
                  <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Switch account</span>
                </div>
                {otherAccounts.map((acct, i) => (
                  <div key={acct.id} className="flex items-center hover:bg-gray-50 transition-colors group">
                    <button
                      onClick={() => { setOpen(false); switchAccount(acct); }}
                      className="flex-1 flex items-center gap-3 px-4 py-2 min-w-0"
                    >
                      <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${avatarColor(i + 1)} flex items-center justify-center flex-shrink-0`}>
                        <span className="text-white text-xs font-semibold">{acct.initial || acct.email?.[0]?.toUpperCase() || '?'}</span>
                      </div>
                      <div className="min-w-0 text-left">
                        <div className="text-sm text-gray-700 truncate">{acct.displayName || acct.email?.split('@')[0]}</div>
                        <div className="text-xs text-gray-400 truncate">{acct.email}</div>
                      </div>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeLinkedAccount(acct.id); }}
                      className="px-3 py-2 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                      title="Remove from list"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add another account */}
            <button
              onClick={handleAddAccount}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add another account
            </button>

            <div className="border-t border-gray-100 mt-1 pt-1">
              <a
                href="/account"
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                onClick={() => setOpen(false)}
              >
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Account & Settings
              </a>
              {(user?.isSuperAdmin || user?.organizations?.some(o => o.role === 'owner' || o.role === 'admin')) && (
                <a
                  href="/admin"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  onClick={() => setOpen(false)}
                >
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  Admin
                </a>
              )}
              {user?.isSuperAdmin && (
                <a
                  href="/admin/bugs"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  onClick={() => setOpen(false)}
                >
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  Bug Tracker
                </a>
              )}
              <a
                href="/account/contributions"
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                onClick={() => setOpen(false)}
              >
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Contributions
              </a>
              {committeeCount > 0 && (
                <a
                  href="/account/contributions"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  onClick={() => setOpen(false)}
                >
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Committees
                  <span className="ml-auto bg-indigo-100 text-indigo-700 text-[10px] font-bold rounded-full px-1.5 py-0.5">{committeeCount}</span>
                </a>
              )}
            </div>
            <div className="border-t border-gray-100 mt-1 pt-1">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sign out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Navigation component
// App hosts: the app's own logo or a neutral monogram. Jubly hosts: the Jubly logo.
function BrandLogo({ size = 28 }) {
  return <BrandMark size={size} />;
}

function Navigation() {
  const { isAuthenticated, user } = useAuth();
  const branding = useBranding();
  const location = useLocation();

  if (!isAuthenticated) return null;

  // Map sections to their canonical subdomains
  const subdomainMap = {
    '/email': 'mail',
    '/calendar': 'calendar',
    '/voice': 'voice',
    '/admin': 'admin',
  };

  const navLink = (to, label) => {
    const active = location.pathname.startsWith(to);
    const hostname = window.location.hostname;
    const baseDomain = hostname.split('.').slice(-2).join('.');
    const currentSub = hostname.split('.').length > 2 ? hostname.split('.')[0] : null;
    const targetSub = subdomainMap[to];

    // If we're on a subdomain and the target is a different section, use full URL
    const needsRedirect = targetSub && currentSub && currentSub !== targetSub;
    const href = needsRedirect ? `https://${targetSub}.${baseDomain}${to}` : to;

    if (needsRedirect) {
      return (
        <a
          href={href}
          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            active ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          {label}
        </a>
      );
    }

    return (
      <Link
        to={to}
        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
          active ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
        }`}
      >
        {label}
      </Link>
    );
  };

  const initial = (user?.displayName || user?.emailAddress || '?')[0].toUpperCase();

  const emailDomain = user?.emailAddress?.split('@')[1];
  const hasCustomDomain = emailDomain && emailDomain !== 'jubly.me' && emailDomain !== 'jubly.net';
  const domainConnected = user?.organizations?.some(o => o.domainConnected && (!o.domain || o.domain === emailDomain));
  const showDomainBanner = hasCustomDomain && !domainConnected && !location.pathname.startsWith('/admin');

  // Sales sub-nav: show when on any /sales/* route
  const inSales = location.pathname.startsWith('/sales');

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      <nav className="bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center gap-2.5">
                <BrandLogo size={28} />
                <span className="font-bold text-gray-900 text-[15px]">{branding.name}</span>
              </Link>

              <div className="hidden sm:flex items-center gap-0.5">
                {navLink('/email', 'Mail')}
                {navLink('/calendar', 'Calendar')}
                {VOICE_ENABLED && navLink('/voice', 'Voice')}
                {navLink('/work', 'Work')}
                {navLink('/meet', 'Meetings')}
                {navLink('/workflows', 'Workflows')}
                {navLink('/sales', 'Sales')}
                {navLink('/marketing', 'Marketing')}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {VOICE_ENABLED && <AvailabilityIndicator />}
              <UserMenu user={user} initial={initial} />
            </div>
          </div>
        </div>
      </nav>

      {/* Sales sub-navigation */}
      {inSales && (
        <div className="bg-white border-b border-gray-100 shadow-sm">
          <div className="px-4 sm:px-6">
            <div className="flex items-center gap-0.5 h-9 overflow-x-auto">
              {[
                { to: '/sales',            label: 'Dashboard' },
                { to: '/sales/contacts',   label: 'Contacts' },
                { to: '/sales/companies',  label: 'Companies' },
                { to: '/sales/pipeline',   label: 'Pipeline' },
                { to: '/sales/tasks',      label: 'Tasks' },
                { to: '/sales/campaigns',  label: 'Campaigns' },
                { to: '/sales/reports',    label: 'Reports' },
              ].map(({ to, label }) => {
                const active = to === '/sales'
                  ? location.pathname === '/sales'
                  : location.pathname.startsWith(to);
                return (
                  <Link
                    key={to}
                    to={to}
                    className={`px-3 py-1 text-xs font-medium rounded-md whitespace-nowrap transition-colors ${
                      active
                        ? 'bg-indigo-50 text-indigo-700'
                        : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    {label}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {showDomainBanner && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="px-4 sm:px-6 py-2 flex items-center justify-center gap-2 text-sm text-amber-800">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <span>Your domain <strong>{emailDomain}</strong> has not been connected yet.</span>
            <Link to="/admin" className="font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-700">
              Connect now
            </Link>
          </div>
        </div>
      )}

      {/* Mobile bottom nav */}
      {isAuthenticated && (
        <nav className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50">
          <div className="flex items-center justify-around h-14">
            {[
              { to: '/email', label: 'Mail', d: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
              { to: '/calendar', label: 'Calendar', d: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
              ...(VOICE_ENABLED ? [{ to: '/voice', label: 'Voice', d: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z' }] : []),
              { to: '/work', label: 'Work', d: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z' },
              { to: '/workflows', label: 'Workflows', d: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
              { to: '/sales', label: 'Sales', d: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
              { to: '/marketing', label: 'Marketing', d: 'M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z' },
            ].map(item => {
              const active = location.pathname.startsWith(item.to);
              return (
                <Link key={item.to} to={item.to} className={`flex flex-col items-center gap-0.5 px-2 py-1 ${active ? 'text-blue-600' : 'text-gray-400'}`}>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={active ? 2 : 1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={item.d} />
                  </svg>
                  <span className="text-[10px] font-medium">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}

// jubly.org — Mission page: Shalom theology & helping the vulnerable
function JublyOrgPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <JublyLogo size={28} />
            <span className="font-bold text-gray-900">jubly.org</span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <a href="https://shalomcapital.net" className="text-gray-500 hover:text-gray-900">Shalom Capital</a>
            <a href="https://jubly.net" className="text-gray-500 hover:text-gray-900">Jubly</a>
            <a href="https://jubly.net/equity" className="text-blue-600 hover:text-blue-800 font-medium">Equity Model</a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-800 text-xs font-medium px-3 py-1.5 rounded-full mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
            A Shalom Capital initiative
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight">
            Lifting up those who<br />have been left behind
          </h1>
          <p className="mt-6 text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">
            jubly.org exists to direct resources toward the most vulnerable — not as charity,
            but as investment in human dignity. One-third of every dollar Jubly earns flows here.
          </p>
        </div>
      </section>

      {/* The Jubilee Principle */}
      <section className="py-16 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">The Jubilee Principle</h2>
          <blockquote className="border-l-4 border-amber-400 pl-6 py-2 mb-8">
            <p className="text-gray-700 italic leading-relaxed">
              "The Spirit of the Lord is on me, because he has anointed me to proclaim good news to the poor.
              He has sent me to proclaim freedom for the prisoners and recovery of sight for the blind,
              to set the oppressed free, to proclaim the year of the Lord's favor."
            </p>
            <footer className="mt-2 text-sm text-gray-500">Luke 4:18-19 / Isaiah 61:1-2</footer>
          </blockquote>
          <div className="prose prose-gray max-w-none text-gray-600 space-y-4">
            <p>
              In ancient Israel, the Year of Jubilee was a radical economic reset. Every fifty years, debts were forgiven,
              land was returned to its original families, and those who had lost everything were restored to wholeness.
              It was a system designed to prevent permanent poverty and ensure that no one was left behind for generations.
            </p>
            <p>
              jubly.org carries this principle into the modern economy. We believe technology should lift people up,
              not extract from them. The way of Shalom is a way in which those who are wealthy understand that they're
              not wealthy because they're smart or brilliant — but because someone was generous towards them.
            </p>
          </div>
        </div>
      </section>

      {/* What is Shalom */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-10 text-center">What is Shalom?</h2>
          <div className="grid sm:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Harmony</h3>
              <p className="text-sm text-gray-500">Peace with God, with ourselves, and with each other. The foundation everything else is built on.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-green-50 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Well-being</h3>
              <p className="text-sm text-gray-500">Health, having enough, and being enough. Not abundance for its own sake, but sufficiency that frees.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Wholeness</h3>
              <p className="text-sm text-gray-500">When harmony and well-being come together — completeness. Every person fully restored and flourishing.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How the fund works */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">How the Vulnerable Fund Works</h2>
          <p className="text-gray-500 text-center mb-10 max-w-xl mx-auto">
            This is not charity. It's covenanted investment in lifting the barriers that keep people from flourishing.
          </p>

          <div className="bg-white rounded-2xl border border-gray-200 p-8 mb-8">
            <div className="flex items-center justify-center gap-4 mb-6 flex-wrap">
              <div className="text-center px-4">
                <div className="text-3xl font-bold text-gray-900">$135</div>
                <div className="text-xs text-gray-500 mt-1">Customer pays</div>
              </div>
              <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
              <div className="text-center px-4">
                <div className="text-3xl font-bold text-gray-400">$100</div>
                <div className="text-xs text-gray-500 mt-1">Cost</div>
              </div>
              <span className="text-gray-300 text-xl">+</span>
              <div className="text-center px-4">
                <div className="text-3xl font-bold text-blue-600">$35</div>
                <div className="text-xs text-gray-500 mt-1">Margin (35%)</div>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-center">
                <div className="text-xl font-bold text-amber-700">~$12</div>
                <div className="text-xs text-amber-600 font-medium mt-1">jubly.org</div>
                <div className="text-[11px] text-amber-500 mt-1">Empower the vulnerable</div>
              </div>
              <div className="rounded-xl bg-blue-50 border border-blue-200 p-4 text-center">
                <div className="text-xl font-bold text-blue-700">~$12</div>
                <div className="text-xs text-blue-600 font-medium mt-1">Dividends</div>
                <div className="text-[11px] text-blue-500 mt-1">To contributors</div>
              </div>
              <div className="rounded-xl bg-green-50 border border-green-200 p-4 text-center">
                <div className="text-xl font-bold text-green-700">~$12</div>
                <div className="text-xs text-green-600 font-medium mt-1">Investment</div>
                <div className="text-[11px] text-green-500 mt-1">Future growth</div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">Where the vulnerable fund goes:</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { icon: '🎓', title: 'Education & Scholarships', desc: 'Removing barriers to learning and skill development' },
                { icon: '💧', title: 'Clean Water & Healthcare', desc: 'Meeting basic needs that unlock human potential' },
                { icon: '🏦', title: 'Microfinance & Small Business', desc: 'Capital for entrepreneurs who banks won\'t serve' },
                { icon: '🏗️', title: 'Community Infrastructure', desc: 'Building the foundations communities need to thrive' },
              ].map((item) => (
                <div key={item.title} className="flex gap-3 bg-white rounded-xl border border-gray-200 p-4">
                  <span className="text-2xl flex-shrink-0">{item.icon}</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{item.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The Corn Story */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Why Investment, Not Charity</h2>
          <div className="prose prose-gray max-w-none text-gray-600 space-y-4">
            <p>
              There's a difference between giving someone tortillas (provision for a day), flour (provision for weeks),
              a harvest (provision for months), and <em>land</em> (provision for generations). Most charity gives tortillas.
              The Jubilee gives land back.
            </p>
            <p>
              We've observed a pattern: the less vulnerable we become, the less grateful we become, and the less generous
              we become. The purpose of wealth in the biblical tradition is not accumulation — it's stewardship.
              Those who have been given much are expected to build systems that lift others, not walls that protect themselves.
            </p>
            <p>
              jubly.org is not a fund that writes checks and walks away. It's a commitment to invest in the invisible
              barriers — lack of education, healthcare, capital, infrastructure — that prevent vulnerable communities
              from participating fully in the economy. When those barriers fall, everyone flourishes.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Join the Movement</h2>
          <p className="text-gray-500 mb-8">
            Every contribution to Jubly — code, ideas, support, partnerships — earns real ownership
            and funds the vulnerable fund. This isn't just a business model. It's a covenant.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <a href="https://jubly.net/signup" className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 transition-colors shadow-sm">
              Get started with Jubly
            </a>
            <a href="https://jubly.net/equity" className="px-6 py-3 bg-white text-gray-700 rounded-xl font-semibold text-sm border border-gray-200 hover:bg-gray-50 transition-colors">
              Read the Equity Constitution
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between text-xs text-gray-400">
          <div className="flex items-center gap-2">
            <JublyLogo size={16} />
            <span>jubly.org — A Shalom Capital initiative</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="https://shalomcapital.net" className="hover:text-gray-600">Shalom Capital</a>
            <a href="https://jubly.net" className="hover:text-gray-600">Jubly</a>
            <a href="https://jubly.net/equity" className="hover:text-gray-600">Equity</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Redirect /meeting/:slug?token=xxx → /meet?join=slug&token=xxx
function MeetingRedirect() {
  const { slug } = useParams();
  const token = new URLSearchParams(window.location.search).get('token');
  const params = `join=${slug}${token ? `&token=${token}` : ''}`;
  return <Navigate to={`/meet?${params}`} replace />;
}

// Landing page for non-authenticated users
function LandingPage() {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to={getDefaultRoute()} replace />;
  }

  // jubly.org → show the jubly.org mission page
  const hostname = window.location.hostname;
  const baseDomain = hostname.split('.').slice(-2).join('.');
  if (baseDomain === 'jubly.org') {
    return <JublyOrgPage />;
  }
  // On other custom domains (not jubly.net), skip landing page → go to login
  if (baseDomain !== 'jubly.net' && hostname !== 'localhost' && hostname !== '127.0.0.1') {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-sm border-b border-gray-100 z-50">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <JublyLogo size={28} />
            <span className="font-bold text-gray-900 text-[15px]">Jubly</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/equity" className="text-sm text-gray-500 hover:text-gray-900 transition-colors hidden sm:block">Equity Model</a>
            <a href="/login" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Sign in</a>
            <a href="/signup" className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 transition-colors font-medium">Get Started</a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-800 text-xs font-medium px-3 py-1.5 rounded-full border border-amber-200 mb-6">
            <span>🌱</span> A Shalom Capital company — <a href="/equity" className="underline">learn about our equity model</a>
          </div>
          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6 leading-[1.1] tracking-tight">
            Communication tools<br />
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">owned by the people</span><br />
            who build them.
          </h1>
          <p className="text-xl text-gray-500 mb-10 max-w-2xl mx-auto leading-relaxed">
            Email, calendar, voice, SMS, video meetings, and collaborative workspaces — all in one place. Every contribution you make earns real ownership. No extraction. No surveillance. Just tools that work.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="/signup" className="px-8 py-3.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-lg text-base">
              Start for free
            </a>
            <a href="/login" className="px-8 py-3.5 bg-white text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors border border-gray-200 text-base">
              Sign in
            </a>
            <a href="/equity" className="px-8 py-3.5 bg-white text-gray-500 font-medium rounded-xl hover:bg-gray-50 transition-colors border border-gray-200 text-base">
              How equity works →
            </a>
          </div>
        </div>
      </section>

      {/* Product Cards */}
      <section className="pb-20 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            {
              title: 'Mail',
              desc: 'A Gmail-quality inbox with keyboard shortcuts, labels, filters, signatures, and undo send. Your data stays yours.',
              href: '/login',
              path: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
            },
            {
              title: 'Calendar',
              desc: 'Google Calendar-style views — month, week, day, schedule. Drag to create events. Subscribe from iPhone or Android.',
              href: '/login',
              path: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
            },
            {
              title: 'Meet',
              desc: 'Video meetings with screen sharing, recording, AI summaries, polls, Q&A, and breakout rooms. No download required.',
              href: '/login',
              path: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z',
            },
            ...(VOICE_ENABLED ? [{
              title: 'Voice & SMS',
              desc: 'Get a real phone number. Make calls from your browser, send texts, get voicemails with transcription.',
              href: '/login',
              path: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
            }] : []),
            {
              title: 'Work',
              desc: 'Files, documents, spreadsheets, and presentations for your team. Upload, preview, and share — all in one place.',
              href: '/login',
              path: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z',
            },
            {
              title: 'Brainstorm',
              desc: 'Visual canvas with framework zones, sticky notes, and AI-powered organization. Paste any text and watch it become structured ideas.',
              href: '/login',
              path: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
            },
            {
              title: 'Collective Docs',
              desc: 'Controlled collaborative editing with inline suggestions, voting, and role-based access. Public contributors suggest — owners decide.',
              href: '/login',
              path: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
            },
            {
              title: 'Collective Pages',
              desc: 'Capture any web page and annotate it together. Review designs, audit content, or mark up competitor sites as a team.',
              href: '/login',
              path: 'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9',
            },
          ].map(product => (
            <a key={product.title} href={product.href}
              className="group bg-white border border-gray-100 rounded-2xl p-6 hover:shadow-md hover:border-gray-200 transition-all">
              <div className="w-10 h-10 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center mb-4">
                <svg className="h-5 w-5 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={product.path} />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1.5 group-hover:text-blue-600 transition-colors">{product.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{product.desc}</p>
            </a>
          ))}
        </div>
      </section>

      {/* Equity Section */}
      <section className="bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 text-white py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">The Jubilee Model</h2>
            <p className="text-blue-200 text-lg max-w-2xl mx-auto">
              Named after the biblical Year of Jubilee — where debts are forgiven and those who have nothing are restored. Every dollar of profit is split three ways:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6 text-center border border-white/10">
              <div className="text-3xl mb-3">🌱</div>
              <h4 className="font-bold text-lg mb-1">jubly.org</h4>
              <p className="text-sm text-blue-200">⅓ of profit empowers vulnerable communities through grants, scholarships, and partnerships.</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6 text-center border border-white/10">
              <div className="text-3xl mb-3">💰</div>
              <h4 className="font-bold text-lg mb-1">Dividends</h4>
              <p className="text-sm text-blue-200">⅓ distributed quarterly to contributors proportional to their contribution points.</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6 text-center border border-white/10">
              <div className="text-3xl mb-3">🚀</div>
              <h4 className="font-bold text-lg mb-1">Investment</h4>
              <p className="text-sm text-blue-200">⅓ reinvested into growth, infrastructure, and R&D to keep building better tools.</p>
            </div>
          </div>

          <div className="text-center">
            <p className="text-blue-300 text-sm mb-4">Pricing is transparent: cost + 45% margin. No hidden fees. No data selling. Ever.</p>
            <a href="/equity" className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-900 font-semibold rounded-xl hover:bg-blue-50 transition-colors shadow-lg">
              Read the full Equity Constitution
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
            </a>
            <p className="text-blue-400/60 text-xs mt-6 max-w-lg mx-auto">
              This ownership model is experimental and in beta. Jubly LLC is not yet formally established — we are actively exploring the best legal structure. Early sign-ups are pre-contributors whose points will be honored when the entity is formed.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">How you earn ownership</h2>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Use Jubly', desc: 'Sign up and use Mail, Calendar, Meet, or Voice for your team.' },
              { step: '2', title: 'Contribute', desc: 'Report bugs, suggest features, help others, write docs — anything.' },
              { step: '3', title: 'Earn Points', desc: 'Every contribution earns weighted points. Code is 3x, bugs 2x, ideas 1-3x.' },
              { step: '4', title: 'Get Dividends', desc: 'Each quarter, ⅓ of profit is distributed proportionally to contributors.' },
            ].map(s => (
              <div key={s.step} className="text-center">
                <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 font-bold text-sm flex items-center justify-center mx-auto mb-3">{s.step}</div>
                <h4 className="font-semibold text-gray-900 mb-1">{s.title}</h4>
                <p className="text-sm text-gray-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-12 px-6">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <JublyLogo size={20} />
            <span className="text-sm text-gray-500">Jubly — Communication tools owned by the people who build them.</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-400">
            <a href="/equity" className="hover:text-gray-700 transition-colors">Equity Model</a>
            <a href="/logo" className="hover:text-gray-700 transition-colors">Our Logo</a>
            <a href="/privacy" className="hover:text-gray-700 transition-colors">Privacy</a>
            <a href="/terms" className="hover:text-gray-700 transition-colors">Terms</a>
            <a href="/login" className="hover:text-gray-700 transition-colors">Sign in</a>
          </div>
        </div>
        <div className="max-w-5xl mx-auto mt-6 pt-6 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400 italic">"The Spirit of the Lord is on me, because he has anointed me to proclaim good news to the poor." — Luke 4:18</p>
        </div>
      </footer>
    </div>
  );
}

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    const baseDomain = parts.length > 2 ? parts.slice(-2).join('.') : hostname;
    const isJublyDomain = baseDomain === 'jubly.net' || hostname === 'localhost';
    const isSubdomain = parts.length > 2 && !hostname.startsWith('www.');

    if (isSubdomain && isJublyDomain) {
      // On jubly.net subdomains (mail.jubly.net), redirect to main domain login
      window.location.href = `https://jubly.net/login`;
      return null;
    }
    // On custom domains (mail.shalomcapital.net) or root, show login in-place
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AvailabilityIndicator() {
  const { user } = useAuth();
  const [dnd, setDnd] = useState(() => localStorage.getItem('jubly_dnd') === 'true');

  if (!user) return null;

  function toggleDnd() {
    const next = !dnd;
    setDnd(next);
    localStorage.setItem('jubly_dnd', next ? 'true' : '');
    // Dispatch event so IncomingCallOverlay picks up the change
    window.dispatchEvent(new Event('jubly_dnd_change'));
  }

  return (
    <span
      onClick={(e) => { e.stopPropagation(); toggleDnd(); }}
      className={`flex-shrink-0 flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium cursor-pointer transition-colors ${
        dnd
          ? 'bg-red-50 text-red-600 hover:bg-red-100'
          : 'bg-green-50 text-green-600 hover:bg-green-100'
      }`}
      title={dnd ? 'Do Not Disturb — calls go to voicemail. Click to turn off.' : 'Available — calls will ring. Click for Do Not Disturb.'}
    >
      {dnd ? (
        <>
          <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" /></svg>
          DND
        </>
      ) : (
        <>
          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
          Available
        </>
      )}
    </span>
  );
}

function IncomingCallOverlay() {
  const { user } = useAuth();
  const [incomingCall, setIncomingCall] = useState(null);
  const [dnd, setDnd] = useState(() => localStorage.getItem('jubly_dnd') === 'true');
  const [connected, setConnected] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const socketRef = React.useRef(null);

  // Sync DND state from localStorage changes
  React.useEffect(() => {
    function onDndChange() { setDnd(localStorage.getItem('jubly_dnd') === 'true'); }
    window.addEventListener('jubly_dnd_change', onDndChange);
    return () => window.removeEventListener('jubly_dnd_change', onDndChange);
  }, []);

  React.useEffect(() => {
    if (!user?.id) return;
    let socket;
    (async () => {
      try {
        const { io } = await import('socket.io-client');
        socket = io(window.location.origin, { path: '/socket.io', transports: ['websocket', 'polling'] });
        socket.on('connect', () => { socket.emit('register', user.id); setConnected(true); });
        socket.on('disconnect', () => setConnected(false));
        socket.on('call_ended', () => {
          // Suppress call_ended for 30s after an incoming call notification
          // TeXML fires multiple status events that prematurely clear state
          if (window.__jublyIncomingCallTime && (Date.now() - window.__jublyIncomingCallTime) < 30000) {
            console.log('Call ended (server) — suppressed (incoming call active)');
            return;
          }
          if (window.__jublyPendingInboundCall || window.__jublyActiveCallObj) {
            console.log('Call ended (server) — suppressed (WebRTC call active)');
            return;
          }
          console.log('Call ended (server) — clearing state');
          window.__jublyActiveCall = null;
          window.dispatchEvent(new Event('jubly_call_update'));
        });
        socket.on('incoming_call', (data) => {
          if (dnd) {
            console.log('Incoming call silenced (DND):', data);
            // Auto-decline to voicemail
            fetch('/api/voice/calls/decline', {
              method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
              body: JSON.stringify({ callControlId: data.callControlId }),
            }).catch(() => {});
            return;
          }
          console.log('Incoming call:', data);
          window.__jublyIncomingCallTime = Date.now();
          setIncomingCall(data);
          // Play subtle ringtone (soft double-ring pattern)
          try {
            if (!window.__jublyRingtone) {
              const ctx = new (window.AudioContext || window.webkitAudioContext)();
              const masterGain = ctx.createGain();
              masterGain.connect(ctx.destination);
              masterGain.gain.value = 0.12; // soft volume

              const playRing = (startTime) => {
                // Double ring: two short tones
                [0, 0.2].forEach(offset => {
                  const osc = ctx.createOscillator();
                  const env = ctx.createGain();
                  osc.connect(env);
                  env.connect(masterGain);
                  osc.frequency.value = 523; // C5 - gentle tone
                  osc.type = 'sine';
                  env.gain.setValueAtTime(0, startTime + offset);
                  env.gain.linearRampToValueAtTime(1, startTime + offset + 0.02);
                  env.gain.setValueAtTime(1, startTime + offset + 0.12);
                  env.gain.linearRampToValueAtTime(0, startTime + offset + 0.15);
                  osc.start(startTime + offset);
                  osc.stop(startTime + offset + 0.15);
                });
              };

              // Ring every 3 seconds for 45 seconds
              let t = ctx.currentTime;
              for (let i = 0; i < 15; i++) {
                playRing(t);
                t += 3;
              }
              window.__jublyRingtone = { ctx };
            }
          } catch (e) { console.warn('Ringtone failed:', e.message); }
        });
        socketRef.current = socket;
      } catch (e) { console.warn('Socket.IO failed:', e.message); }
    })();
    return () => { if (socket) socket.disconnect(); };
  }, [user?.id, dnd]);

  function toggleDnd() {
    const next = !dnd;
    setDnd(next);
    localStorage.setItem('jubly_dnd', next ? 'true' : '');
  }

  if (!user || !incomingCall) return null;

  function stopRingtone() {
    try {
      if (window.__jublyRingtone?.ctx) {
        window.__jublyRingtone.ctx.close();
        window.__jublyRingtone = null;
      }
    } catch {}
  }

  async function handleAccept() {
    stopRingtone();
    setAccepting(true);

    // With TeXML, the SDK already received the inbound call via <Dial><Sip>.
    // Just answer the pending WebRTC call — no server-side action needed.
    const webrtcCall = window.__jublyPendingInboundCall;
    if (webrtcCall) {
      console.log('Answering WebRTC inbound call from TeXML');
      try { webrtcCall.answer(); } catch(e) { console.warn('Answer error:', e); }

      // Ensure local audio is enabled (fix one-way audio)
      setTimeout(() => {
        if (webrtcCall.localStream) {
          webrtcCall.localStream.getAudioTracks().forEach(track => {
            track.enabled = true;
            console.log('Inbound call: local audio track enabled:', track.label, track.enabled);
          });
        }
        // Also attach remote audio to ensure caller is heard
        const audioEl = document.getElementById('telnyx-remote-audio');
        if (webrtcCall.remoteStream && audioEl) {
          audioEl.srcObject = webrtcCall.remoteStream;
          audioEl.play().catch(() => {});
        }
      }, 500);

      window.__jublyActiveCall = { from: incomingCall.from, callerName: incomingCall.callerName };
      window.__jublyActiveCallObj = webrtcCall;
      window.__jublyPendingInboundCall = null;
      window.dispatchEvent(new Event('jubly_call_update'));
      window.dispatchEvent(new Event('jubly_call_obj_update'));
    } else {
      // SDK hasn't received the call yet — poll for it
      console.log('Waiting for WebRTC call from TeXML...');
      window.__jublyActiveCall = { from: incomingCall.from, callerName: incomingCall.callerName };
      window.dispatchEvent(new Event('jubly_call_update'));

      let answered = false;
      const checkInterval = setInterval(() => {
        if (window.__jublyPendingInboundCall) {
          const call = window.__jublyPendingInboundCall;
          console.log('WebRTC call received — answering');
          try { call.answer(); } catch(e) {}
          // Enable local audio
          setTimeout(() => {
            if (call.localStream) {
              call.localStream.getAudioTracks().forEach(t => { t.enabled = true; });
            }
          }, 500);
          window.__jublyActiveCallObj = call;
          window.dispatchEvent(new Event('jubly_call_obj_update'));
          window.__jublyPendingInboundCall = null;
          answered = true;
          clearInterval(checkInterval);
        }
      }, 200);
      setTimeout(() => { clearInterval(checkInterval); if (!answered) console.log('WebRTC call not received'); }, 10000);
    }

    setIncomingCall(null);
    setAccepting(false);
  }

  async function handleDecline() {
    stopRingtone();
    try {
      await fetch('/api/voice/calls/decline', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ callControlId: incomingCall.callControlId }),
      });
      setIncomingCall(null);
    } catch (err) { console.error('Decline failed:', err); }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4 animate-pulse">
          <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        </div>
        <p className="text-lg font-semibold text-gray-900">{incomingCall.callerName}</p>
        <p className="text-sm text-gray-500 mt-1">{incomingCall.from}</p>
        <p className="text-xs text-gray-400 mt-1">Incoming call...</p>
        <div className="flex justify-center gap-6 mt-6">
          <button onClick={handleDecline}
            className="w-14 h-14 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" /></svg>
          </button>
          <button onClick={handleAccept} disabled={accepting}
            className={`w-14 h-14 rounded-full text-white flex items-center justify-center transition-colors shadow-lg ${
              accepting ? 'bg-blue-500 cursor-wait' : 'bg-green-500 hover:bg-green-600 animate-bounce'
            }`}>
            {accepting ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function ActiveCallBar() {
  const [activeCall, setActiveCall] = useState(null);
  const [duration, setDuration] = useState(0);

  React.useEffect(() => {
    function onUpdate() {
      setActiveCall(window.__jublyActiveCall || null);
    }
    window.addEventListener('jubly_call_update', onUpdate);
    onUpdate();
    return () => window.removeEventListener('jubly_call_update', onUpdate);
  }, []);

  React.useEffect(() => {
    if (!activeCall) { setDuration(0); return; }
    const start = Date.now();
    const timer = setInterval(() => setDuration(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [activeCall]);

  if (!activeCall) return null;

  const mins = Math.floor(duration / 60);
  const secs = duration % 60;

  return (
    <div
      className="bg-green-500 text-white text-center py-1.5 text-xs font-medium cursor-pointer hover:bg-green-600 transition-colors"
      onClick={() => { if (window.location.pathname !== '/voice') window.location.href = '/voice'; }}
    >
      <span className="inline-flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
        On call with {activeCall.callerName || activeCall.from} — {mins}:{secs.toString().padStart(2, '0')}
        <span className="ml-2 underline">Return to call</span>
      </span>
    </div>
  );
}

function GlobalCallWindow() {
  const [callObj, setCallObj] = useState(null);
  const [callInfo, setCallInfo] = useState(null);

  React.useEffect(() => {
    function onObjUpdate() { setCallObj(window.__jublyActiveCallObj || null); }
    function onInfoUpdate() { setCallInfo(window.__jublyActiveCall || null); }
    window.addEventListener('jubly_call_obj_update', onObjUpdate);
    window.addEventListener('jubly_call_update', onInfoUpdate);
    onObjUpdate();
    onInfoUpdate();
    return () => {
      window.removeEventListener('jubly_call_obj_update', onObjUpdate);
      window.removeEventListener('jubly_call_update', onInfoUpdate);
    };
  }, []);

  // Show CallWindow if we have a WebRTC call object
  if (callObj) {
    return (
      <CallWindow
        call={callObj}
        onHangup={() => {
          window.__jublyActiveCall = null;
          window.__jublyActiveCallObj = null;
          window.dispatchEvent(new Event('jubly_call_update'));
          window.dispatchEvent(new Event('jubly_call_obj_update'));
      }}
    />
  );
  }

  // Fallback: show a simple hangup-only window for server-side calls without WebRTC
  if (callInfo && callInfo.callControlId) {
    return (
      <div className="fixed bottom-4 right-4 z-[90] w-64 bg-gray-900 rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-4 py-3">
          <p className="text-sm font-medium text-white">{callInfo.callerName || callInfo.from}</p>
          <p className="text-xs text-green-400 mt-0.5">Connected</p>
        </div>
        <div className="flex justify-center p-3 border-t border-gray-800">
          <button onClick={async () => {
            try {
              await fetch(`/api/voice/calls/${callInfo.callControlId}/hangup`, {
                method: 'POST', credentials: 'include',
              }).catch(() => {});
            } catch {}
            window.__jublyActiveCall = null;
            window.__jublyActiveCallObj = null;
            window.dispatchEvent(new Event('jubly_call_update'));
            window.dispatchEvent(new Event('jubly_call_obj_update'));
          }} className="w-10 h-10 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return null;
}

// No sign-up on an app's own domain (mail.datosmcp.com): those mailboxes are provisioned
// by the app, and a self-serve signup there is what made the page look like a phishing kit.
function SignupRoute() {
  const branding = useBranding();
  if (branding.isApp) return <Navigate to="/login" replace />;
  return <SignupPage />;
}

function AppContent() {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const isPublicPage = ['/', '/login', '/signup', '/equity', '/privacy', '/terms', '/logo'].includes(location.pathname);
  const emailDomain = user?.emailAddress?.split('@')[1];
  const hasCustomDomain = emailDomain && emailDomain !== 'jubly.me' && emailDomain !== 'jubly.net';
  const domainConnected = user?.organizations?.some(o => o.domainConnected && (!o.domain || o.domain === emailDomain));
  const hasDomainBanner = isAuthenticated && hasCustomDomain && !domainConnected && !location.pathname.startsWith('/admin');
  const inSales = location.pathname.startsWith('/sales');

  // Extra top padding when sales sub-nav is showing
  const topPadding = isPublicPage ? '' : (hasDomainBanner ? 'pt-24 pb-16 sm:pb-0' : inSales ? 'pt-[88px] pb-16 sm:pb-0' : 'pt-14 pb-16 sm:pb-0');

  // /presenter is a standalone popup page — no nav, no padding
  if (location.pathname === '/presenter') {
    return <PresenterMode />;
  }

  // /embed/slides is a standalone embeddable slide viewer — no nav, no auth required
  if (location.pathname === '/embed/slides') {
    return <EmbedSlideViewer />;
  }

  return (
    <>
      <Navigation />
      {/* Voice product is turned off — skip all Telnyx call UI / SIP registration */}
      {VOICE_ENABLED && (
        <>
          <ActiveCallBar />
          <IncomingCallOverlay />
          {/* Global floating call window — persists across page navigation */}
          <GlobalCallWindow />
          {/* Hidden Dialer — keeps TelnyxRTC SIP registration alive on non-Voice pages */}
          {isAuthenticated && !isPublicPage && !location.pathname.startsWith('/voice') && (
            <div style={{ position: 'fixed', left: '-9999px', width: 0, height: 0, overflow: 'hidden', pointerEvents: 'none' }}>
              <Dialer key="global-dialer" />
            </div>
          )}
        </>
      )}
      <div className={topPadding}>
      <ContributionWidget />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/equity" element={<EquityPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/logo" element={<LogoPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupRoute />} />
          <Route
            path="/email/*" 
            element={
              <ProtectedRoute>
                <EmailApp />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/calendar/*" 
            element={
              <ProtectedRoute>
                <CalendarApp />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/voice/*"
            element={
              VOICE_ENABLED ? (
                <ProtectedRoute>
                  <VoiceApp />
                </ProtectedRoute>
              ) : (
                <Navigate to="/email" replace />
              )
            }
          />
          <Route
            path="/meet/*"
            element={
              <MeetApp />
            }
          />
          <Route
            path="/meeting/:slug"
            element={<MeetingRedirect />}
          />
          <Route
            path="/work/doc/:id"
            element={
              <ProtectedRoute>
                <DocumentEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/work/sheet/:id"
            element={
              <ProtectedRoute>
                <SpreadsheetEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/work/slides/:id"
            element={
              <ProtectedRoute>
                <PresentationEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/work/brainstorm/:id"
            element={
              <ProtectedRoute>
                <BrainstormEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/work/collective/:id"
            element={<CollectiveDocEditor />}
          />
          <Route
            path="/work/page/:id"
            element={<CollectivePageEditor />}
          />
          <Route
            path="/work/*"
            element={
              <ProtectedRoute>
                <WorkApp />
              </ProtectedRoute>
            }
          />
                    {/* Workflows */}
          <Route path="/workflows" element={<ProtectedRoute><WorkflowListWithNav /></ProtectedRoute>} />
          <Route path="/workflows/new" element={<ProtectedRoute><WorkflowBuilder onBack={() => window.history.back()} /></ProtectedRoute>} />
          <Route path="/workflows/ai" element={<ProtectedRoute><WorkflowAiPage /></ProtectedRoute>} />
          <Route path="/workflows/analytics" element={<ProtectedRoute><WorkflowAnalyticsPage /></ProtectedRoute>} />
          <Route path="/workflows/templates" element={<ProtectedRoute><WorkflowTemplatesPage /></ProtectedRoute>} />
          <Route path="/workflows/:id" element={<ProtectedRoute><WorkflowBuilderWithParams /></ProtectedRoute>} />
          <Route path="/workflows/runs/:runId/batch-approve" element={<ProtectedRoute><WorkflowBatchApprovalPage /></ProtectedRoute>} />
          {/* Sales / CRM */}
          <Route
            path="/sales"
            element={
              <ProtectedRoute>
                <SalesDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/contacts"
            element={
              <ProtectedRoute>
                <SalesContacts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/pipeline"
            element={
              <ProtectedRoute>
                <SalesPipeline />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/tasks"
            element={
              <ProtectedRoute>
                <SalesTasks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/companies"
            element={
              <ProtectedRoute>
                <SalesCompanies />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/campaigns"
            element={
              <ProtectedRoute>
                <SalesCampaigns />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/reports"
            element={
              <ProtectedRoute>
                <SalesReports />
              </ProtectedRoute>
            }
          />
          {/* Marketing */}
          <Route
            path="/marketing/*"
            element={
              <ProtectedRoute>
                <MarketingApp />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/contributions"
            element={
              <ProtectedRoute>
                <ContributionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/referrals"
            element={
              <ProtectedRoute>
                <ReferralPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/billing"
            element={
              <ProtectedRoute>
                <BillingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/*"
            element={
              <ProtectedRoute>
                <AccountPage />
              </ProtectedRoute>
            }
          />
          {/* Admin */}
          <Route
            path="/admin/bugs"
            element={
              <ProtectedRoute>
                <AdminBugTracker />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </>
  );
}

function WorkflowListWithNav() {
  const navigate = useNavigate();
  return <WorkflowList onSelect={(wf) => navigate('/workflows/' + wf.id)} />;
}

function WorkflowBuilderWithParams() {
  const { id } = useParams();
  return <WorkflowBuilder workflowId={id} onBack={() => window.history.back()} />;
}


function App() {
  return (
    <BrandingProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </AuthProvider>
    </BrandingProvider>
  );
}

export default App;
// build 1774495603
